import { alpha } from '@mui/material/styles';

// A VALUE CHANGED AND NOT SAVED YET, as the pedigree's data entry grid draws it (Bulk Edit,
// pedigree-chart/BulkEditModal/MemberRow.jsx): a gold outline on a light gold ground. The two
// colours live here so that a changed value reads the same wherever it is drawn: the grid's cells,
// and the Test modal's fields while a lab report is applied to it (TGCLOUD-1866, Tim's QA
// 2026-10-01).

/** The grid's outline for an unsaved change. */
export const UNSAVED_BORDER = '#BA7517';
/** The grid's ground for an unsaved change. */
export const UNSAVED_BG = '#FFF8EF';

/**
 * The ground for an unsaved change, for an sx value. The grid has no dark variant, and its light
 * ground under the dark theme's light text cannot be read, so on the dark theme it is the same gold
 * at low opacity over the dark surface. No new colour: both are UNSAVED_BORDER / UNSAVED_BG.
 * @param {object} theme  the MUI theme
 * @returns {string}
 */
export const unsavedBackground = (theme) =>
  theme?.palette?.mode === 'dark' ? alpha(UNSAVED_BORDER, 0.16) : UNSAVED_BG;

// The outlined inputs a form holds, and each one's outline: MUI's own, and MUI X's date pickers'.
const OUTLINED_INPUTS = [
  ['MuiOutlinedInput-root', 'MuiOutlinedInput-notchedOutline'],
  ['MuiPickersOutlinedInput-root', 'MuiPickersOutlinedInput-notchedOutline']
];

/**
 * sx for an element that HOLDS an outlined input (a TextField, an Autocomplete, a select, a date
 * picker, or the Grid around one): the input's ground is gold, and so is its outline, except while
 * the input is focused (the theme's focus outline stays visible) or in error (the error outline
 * wins). Nothing when `unsaved` is false.
 * @param {boolean} unsaved
 * @returns {object}
 */
export function unsavedFieldSx(unsaved) {
  if (!unsaved) return {};
  const sx = {};
  for (const [root, outline] of OUTLINED_INPUTS) {
    sx[`& .${root}`] = { bgcolor: unsavedBackground };
    sx[`& .${root}:not(.Mui-error):not(.Mui-focused) .${outline}`] = {
      borderColor: UNSAVED_BORDER
    };
  }
  return sx;
}

/**
 * sx for an element that holds a control with no outline of its own (a checkbox, a switch, a list
 * of attachments): the grid's gold outline round it, on the gold ground. An outline, not a border,
 * so nothing moves when it comes or goes. Nothing when `unsaved` is false.
 * @param {boolean} unsaved
 * @returns {object}
 */
export function unsavedControlSx(unsaved) {
  if (!unsaved) return {};
  return {
    bgcolor: unsavedBackground,
    outline: `1px solid ${UNSAVED_BORDER}`,
    borderRadius: 1
  };
}

/**
 * sx for a table row holding an unsaved change: the grid's gold bar down its left edge, on the
 * gold ground. Nothing when `unsaved` is false.
 * @param {boolean} unsaved
 * @returns {object}
 */
export function unsavedRowSx(unsaved) {
  if (!unsaved) return {};
  return {
    borderLeft: `3px solid ${UNSAVED_BORDER}`,
    '& > td': { bgcolor: unsavedBackground }
  };
}

/**
 * sx for a tile (a card) holding an unsaved change: the grid's unsaved card, a gold outline with a
 * heavier left edge, on the gold ground. Spread after the tile's own colours. Nothing when
 * `unsaved` is false.
 * @param {boolean} unsaved
 * @returns {object}
 */
export function unsavedTileSx(unsaved) {
  if (!unsaved) return {};
  return {
    border: `1px solid ${UNSAVED_BORDER}`,
    borderLeft: `3px solid ${UNSAVED_BORDER}`,
    backgroundColor: unsavedBackground
  };
}
